import { z } from "zod";

export interface ManualSyncInput {
  shopIds: string[];
  startDate: string;
  endDate: string;
}

const DAY_MS = 86_400_000;
export const MANUAL_SYNC_PRESETS = [1, 3, 15, 30] as const;

export function isValidManualSyncTimeZone(timeZone: string | null): timeZone is string {
  if (!timeZone || /^[+-]/.test(timeZone)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

function localDay(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value;
  return Date.parse(`${get("year")}-${get("month")}-${get("day")}T00:00:00Z`);
}

export function getManualSyncRange(
  days: number,
  now = new Date(),
  timeZones: readonly string[] = ["UTC"],
) {
  const today = Math.min(...timeZones.map((timeZone) => localDay(now, timeZone)));
  return {
    startDate: new Date(today - days * DAY_MS).toISOString().slice(0, 10),
    endDate: new Date(today - DAY_MS).toISOString().slice(0, 10),
  };
}

export function getManualSyncBounds(now = new Date(), timeZones: readonly string[] = ["UTC"]) {
  const earliestToday = Math.min(...timeZones.map((timeZone) => localDay(now, timeZone)));
  const latestToday = Math.max(...timeZones.map((timeZone) => localDay(now, timeZone)));
  return {
    minDate: new Date(latestToday - 60 * DAY_MS).toISOString().slice(0, 10),
    maxDate: new Date(earliestToday - DAY_MS).toISOString().slice(0, 10),
  };
}

function isValidDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function createManualSyncSchema(
  eligibleShopIds: readonly string[],
  now = new Date(),
  timeZones: readonly string[] = ["UTC"],
) {
  const { minDate, maxDate } = getManualSyncBounds(now, timeZones);
  const date = z.string().refine(isValidDate, "invalidDate");
  return z
    .object({
      shopIds: z
        .array(z.string())
        .min(1, "selectShop")
        .refine((ids) => ids.every((id) => eligibleShopIds.includes(id)), "unavailableShop"),
      startDate: date,
      endDate: date,
    })
    .superRefine((input, context) => {
      if (!isValidDate(input.startDate) || !isValidDate(input.endDate)) return;
      if (input.endDate < input.startDate) {
        context.addIssue({ code: "custom", path: ["endDate"], message: "dateOrder" });
      }
      for (const field of ["startDate", "endDate"] as const) {
        if (input[field] < minDate || input[field] > maxDate) {
          context.addIssue({ code: "custom", path: [field], message: "dateWindow" });
        }
      }
    });
}
